import { Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { CollaboratorsScope, FilterScope } from 'mormaii-web-presentation/collaborators'

import { collaboratorCell } from '../../../components/collaborator-cells'
import { Button, ChoiceFilter, TextFilter } from '../../../components/filters'
import { CompanyPicker } from '../../../components/company-picker'
import { PeriodPicker } from '../../../components/period-picker'
import { DataTable, Pager } from '../../../components/table'
import { ErrorNotice, Menu, Panel } from '../../../components/ui'

import Css from './collaborators.module.scss'

/**
 * A lista de colaboradores.
 *
 * A view nao guarda filtro, ordenacao nem pagina: tudo isso e parametro do place e
 * chega pronto no scope. O que o leitor filtrou viaja no endereco, e o link que ele
 * manda a um colega abre a MESMA lista.
 *
 * As colunas sao dinamicas - as de medicao dependem do que a empresa mede -, e por
 * isso as celulas sao montadas na ordem das colunas e nao por nome.
 */
export function CollaboratorsView(props: ViewProps<CollaboratorsScope>): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSubmit()
    }

    return (
        <Show when={!props.scope.error} fallback={<ErrorNotice message={props.scope.error!} />}>
            <div class={Css.screen} aria-busy={props.scope.loading}>
                {/*
                    A BARRA escura, e nao um cartao branco.
                    
                    E o desenho que as outras tres listas com filtro usam - Empresas,
                    Monitoramentos e Adocao e Engajamento -, e esta era a unica fora
                    dele. A diferenca nao era decisao: o cartao branco veio do painel
                    antigo e ficou quando as outras mudaram.
                */}
                <form onSubmit={submit}>
                    <section class={Css.filterBar}>
                        <div class={Css.haloTop} />
                        <div class={Css.haloBottom} />

                        <div class={Css.filterBarBody}>
                            <h2 class={Css.filterBarTitle}>Filtrar colaboradores</h2>

                            <div class={Css.controls}>
                                <TextFilter
                                    onDark
                                    label="Nome"
                                    placeholder="Busca parcial"
                                    value={props.scope.name}
                                    onChange={(value) => props.scope.onNameChanged(value)}
                                />
                                <TextFilter
                                    onDark
                                    label="CPF"
                                    placeholder="Busca parcial"
                                    value={props.scope.cpf}
                                    onChange={(value) => props.scope.onCpfChanged(value)}
                                />
                                <TextFilter
                                    onDark
                                    label="E-mail"
                                    placeholder="Busca parcial"
                                    value={props.scope.email}
                                    onChange={(value) => props.scope.onEmailChanged(value)}
                                />

                                {/*
                                    `glass` como na barra de Adocao e Engajamento: na
                                    barra escura o seletor de campo branco seria o unico
                                    controle claro da fileira.
                                */}
                                <Show when={props.scope.companyFilter}>
                                    {(selector) => <CompanyPicker scope={selector()} variant="glass" />}
                                </Show>

                                <Choice scope={props.scope.measurementTypeFilter} />
                                <Choice scope={props.scope.statusFilter} />

                                {/*
                                    ANAMNESE - preenchida, sem preencher, ou tanto faz.

                                    Ao lado do status de saude, e nao junto de CADASTRO: os
                                    dois primeiros perguntam da FICHA, e CADASTRO pergunta
                                    da CONTA. A fileira agrupa pelo que se pergunta.

                                    Os dois lados sao opcao porque os dois tem cartao em
                                    Adocao e Engajamento - o degrau "Preencheram a anamnese"
                                    e a lacuna "Sem anamnese" -, e cada um abre esta lista
                                    com o seu.
                                */}
                                <Choice scope={props.scope.anamneseFilter} />

                                {/*
                                    CADASTRO - todos, ou so os ativos.
                                    
                                    Chega ligado de todo cartao de Adocao e Engajamento, e
                                    ate virar controle vivia so como uma frase no aviso
                                    acima da tabela: dava para desligar TUDO pelo "Ver
                                    todos", e nao para desligar so ele.
                                    
                                    "CADASTRO" e o termo daquela tela - "108 com cadastro
                                    ativo". E e ele, e nao o periodo ao lado, que fica com
                                    a palavra "ativo".
                                */}
                                <Choice scope={props.scope.accountsFilter} />

                                {/*
                                    ATIVIDADE - o que o periodo ao lado quer dizer.

                                    Antes dele de proposito: e o verbo da frase que os dois
                                    formam - "nao registrou EM tal periodo". Limpar volta ao
                                    padrao, que e presenca.
                                */}
                                <Choice scope={props.scope.activityFilter} />

                                {/*
                                    O PERIODO de atividade - "quem registrou algo entre
                                    estas datas", ou o contrario, conforme o combo ao lado.
                                    
                                    E o filtro que os cartoes de Adocao e Engajamento
                                    preenchem ao abrir esta lista. Sem ele na tela, aquele
                                    recorte chegava e nao aparecia em controle nenhum:
                                    quem clicava num cartao procurava as datas aqui e nao
                                    as encontrava.
                                    
                                    Sem rotulo, era o unico controle anonimo da
                                    fileira - duas datas soltas entre filtros nomeados, e
                                    quem chegava de um cartao nao tinha como saber que
                                    aquilo era o recorte de atividade.
                                    
                                    "REGISTROU EM" e nao "ATIVO EM", que era o nome do
                                    cartao de origem: com o filtro de CADASTRO ao lado, a
                                    fileira passaria a dizer "ativo" duas vezes com dois
                                    sentidos - o ESTADO do cadastro e o COMPORTAMENTO da
                                    pessoa -, e um do lado do outro isso sugere uma
                                    relacao que nao existe. Os dois criterios sao
                                    independentes: cadastro ativo sem registro nenhum no
                                    periodo passa num e e cortado pelo outro.
                                    
                                    O verbo e o que a propria Adocao e Engajamento usa
                                    para este numero: "usuarios que REGISTRARAM medicao ou
                                    treino dentro da janela escolhida" e o texto de ajuda
                                    do cartao "Ativos (N dias)".

                                    O rotulo vem do SCOPE porque ele segue o combo: com a
                                    ausencia escolhida ele vira "SEM REGISTRO EM", senao a
                                    fileira diria "nao registrou" e "registrou em" lado a
                                    lado.
                                */}
                                <Show when={props.scope.period}>
                                    {(period) => (
                                        <PeriodPicker
                                            scope={period()}
                                            onDark
                                            label={props.scope.periodLabel}
                                            class={Css.period}
                                        />
                                    )}
                                </Show>

                                {/*
                                    O periodo de CADASTRO - "quem entrou na base entre
                                    estas datas".
                                    
                                    Dois seletores e nao um com chave: sao dois eixos
                                    diferentes no servidor, e uma pessoa pode casar um e
                                    nao o outro. Com um par de datas so, "cadastrados em
                                    setembro E que registraram algo esta semana" deixaria
                                    de ser exprimivel.
                                */}
                                <Show when={props.scope.registrationPeriod}>
                                    {(period) => (
                                        <PeriodPicker
                                            scope={period()}
                                            onDark
                                            label="Cadastrado em"
                                            class={Css.period}
                                        />
                                    )}
                                </Show>

                                {/*
                                    So o Limpar, como em Empresas e nos Monitoramentos: o
                                    filtro vale sozinho conforme se mexe nele - o texto
                                    depois de parar de digitar, os seletores na hora. O
                                    "Aplicar busca" era a ultima diferenca de
                                    comportamento entre as tres listas.
                                */}
                                <Button
                                    variant="glass"
                                    class={Css.clear}
                                    onClick={() => void props.scope.onClearFilters()}
                                >
                                    Limpar
                                </Button>
                            </div>
                        </div>
                    </section>
                </form>

                <Panel
                    title="Lista de colaboradores"
                    flush
                    actions={
                        /*
                            Quais medicoes viram coluna (B-42). Com um indicador em foco
                            - a lista aberta pela barra do Dashboard -, ele vem marcado e
                            travado, e o resto se inclui por aqui.
                        */
                        <Menu
                            multiple
                            summary="Colunas"
                            options={props.scope.columnChoices.map((choice) => ({
                                label: choice.label,
                                current: choice.checked,
                                disabled: choice.locked,
                                onSelect: choice.onToggle
                            }))}
                        />
                    }
                >
                    {/*
                        O recorte que veio de um cartao de Adocao e Engajamento, dito em
                        palavras. Fica ACIMA da tabela, entre o titulo e os dados: e a
                        primeira coisa a ler quando a lista esta mais curta do que a
                        conhecida, e o "Ver todos" sai dela sem passar pelo formulario.
                    */}
                    <Show when={props.scope.contextNotice}>
                        {(notice) => (
                            <div class={Css.contextNotice}>
                                <span class={Css.contextText}>{notice()}</span>
                                <button
                                    type="button"
                                    class={Css.contextClear}
                                    onClick={() => void props.scope.onClearContext()}
                                >
                                    Ver todos
                                </button>
                            </div>
                        )}
                    </Show>

                    <DataTable
                        columns={props.scope.columns.map((column) => ({
                            field: column.field,
                            label: column.label,
                            sortable: column.sortable,
                            sorted: column.sorted,
                            descending: column.descending,
                            onSort: column.onSort,
                            // As medicoes se arrastam para mudar de lugar (B-42), como na ficha.
                            movable: column.movable,
                            onDrop: (source) => column.onDrop(source)
                        }))}
                        // A geometria das colunas vem do modelo da tabela, na apresentacao.
                        layout={props.scope.layout}
                        rows={props.scope.rows}
                        loading={props.scope.loading}
                        cell={collaboratorCell}
                        emptyText={
                            props.scope.anyFilterActive
                                ? 'Nenhum colaborador encontrado com os filtros aplicados'
                                : 'Nenhum colaborador para mostrar'
                        }
                    />

                    <Pager
                        page={props.scope.page}
                        pageCount={props.scope.pageCount}
                        pages={props.scope.pageWindow}
                        total={props.scope.total}
                        onPrevious={() => void props.scope.onPreviousPage()}
                        onNext={() => void props.scope.onNextPage()}
                        onGoTo={(page) => void props.scope.onGoToPage(page)}
                    />
                </Panel>
            </div>
        </Show>
    )
}

function Choice(props: { scope?: FilterScope }): JSX.Element {
    return (
        <Show when={props.scope}>
            {(scope) => (
                <ChoiceFilter
                    onDark
                    label={scope().label}
                    summary={scope().summary}
                    placeholder={scope().placeholder}
                    hasValue={!!scope().value}
                    disabled={!scope().enabled}
                    onClear={() => void scope().onClear()}
                    options={scope().options.map((option) => ({
                        label: option.label,
                        value: option.value,
                        current: option.current,
                        onSelect: option.onSelect
                    }))}
                />
            )}
        </Show>
    )
}
